/**
 * Content service for loading static JSON content (situations, activities)
 */

export interface SensoryExplanation {
  headline: string
  body: string
  tip: string
}

export interface Situation {
  id: string
  title: string
  description: string
  icon: string
  sensoryExplanation: SensoryExplanation
  order: number
}

export interface ActivityStep {
  step: number
  instruction: string
}

// Raw activity from JSON (steps are strings)
interface RawActivity {
  id: string
  title: string
  description?: string
  duration: number
  situationIds: string[]
  sensoryTypes: string[]
  props: string[]
  steps: string[]
  whyItWorks: string
  whenToUse: string
  isPremium: boolean
  order: number
  images?: string[]
}

export interface Activity {
  id: string
  title: string
  description: string
  duration: number
  situationIds: string[]
  sensoryTypes: string[]
  props: string[]
  steps: ActivityStep[]
  whyItWorks: string
  whenToUse: string
  isPremium: boolean
  order: number
  images?: string[]
}

interface SituationsData {
  situations: Situation[]
}

interface ActivitiesData {
  activities: RawActivity[]
}

// Program types
export interface ProgramDay {
  day: number
  title: string
  description: string
  activities: string[]
  tip: string
  milestone: string | null
}

export interface Program {
  id: string
  title: string
  subtitle: string
  description: string
  duration: number
  situationIds: string[]
  icon: string
  isPremium: boolean
  coverColor: string
  linkedPrintableIds: string[]
  days: ProgramDay[]
  coverImage: string
  highlights: string[]
  whatToExpect: string[]
  whoIsThisFor: string[]
  methodology: string[]
  expectedOutcomes: string[]
  sectionImages: string[]
}

// Printable types
export interface Printable {
  id: string
  title: string
  description: string
  category: string
  pages: number
  situationIds: string[]
  isPremium: boolean
  filename: string
  tags: string[]
  popular: boolean
}

// DIY Activity types
export interface DiyCategory {
  id: string
  title: string
  emoji: string
  color: string
  description: string
}

export interface DiyActivity extends Activity {
  categoryId: string
  ageRange: { min: number; max: number }
}

interface DiyActivitiesData {
  categories: DiyCategory[]
  activities: RawActivity & { categoryId: string; ageRange: { min: number; max: number } }[]
}

// Toy types
export interface Toy {
  id: string
  name: string
  description: string
  whyScience: string
  developmentAreas: string[]
  ageRange: { min: number; max: number }
  amazonUrl: string
  emoji: string
}

interface ToysData {
  toys: Toy[]
}

// Book types
export interface Book {
  id: string
  title: string
  author: string
  description: string
  whyRecommended: string
  categories: string[]
  ageRange: { min: number; max: number }
  amazonUrl: string
  emoji: string
}

interface BooksData {
  books: Book[]
}

// Cache for loaded content
let toysCache: Toy[] | null = null
let booksCache: Book[] | null = null
let situationsCache: Situation[] | null = null
let activitiesCache: Activity[] | null = null
let programsCache: Program[] | null = null
let printablesCache: Printable[] | null = null
let diyActivitiesCache: DiyActivity[] | null = null
let diyCategoriesCache: DiyCategory[] | null = null

// Transform raw activity (string steps) to Activity (object steps)
function transformActivity(raw: RawActivity): Activity {
  return {
    ...raw,
    description: raw.description || '',
    steps: raw.steps.map((instruction, index) => ({
      step: index + 1,
      instruction,
    })),
    images: raw.images,
  }
}

export async function loadSituations(): Promise<Situation[]> {
  if (situationsCache) return situationsCache

  const response = await fetch('/content/situations.json')
  const data: SituationsData = await response.json()
  situationsCache = data.situations.sort((a, b) => a.order - b.order)
  return situationsCache
}

export async function loadActivities(): Promise<Activity[]> {
  if (activitiesCache) return activitiesCache

  const response = await fetch('/content/activities.json')
  const data: ActivitiesData = await response.json()
  activitiesCache = data.activities
    .map(transformActivity)
    .sort((a, b) => a.order - b.order)
  return activitiesCache
}

export async function getSituation(id: string): Promise<Situation | undefined> {
  const situations = await loadSituations()
  return situations.find(s => s.id === id)
}

export async function getActivity(id: string): Promise<Activity | undefined> {
  const activities = await loadActivities()
  const found = activities.find(a => a.id === id)
  if (found) return found
  // Fall back to DIY activities
  const diyActivities = await loadDiyActivities()
  return diyActivities.find(a => a.id === id)
}

export async function getActivitiesForSituation(situationId: string): Promise<Activity[]> {
  const activities = await loadActivities()
  return activities.filter(a => a.situationIds.includes(situationId))
}

export async function getFreeActivitiesForSituation(situationId: string): Promise<Activity[]> {
  const activities = await getActivitiesForSituation(situationId)
  return activities.filter(a => !a.isPremium).slice(0, 2)
}

export async function getPremiumActivitiesForSituation(situationId: string): Promise<Activity[]> {
  const activities = await getActivitiesForSituation(situationId)
  return activities.filter(a => a.isPremium)
}

// Programs
export async function loadPrograms(): Promise<Program[]> {
  if (programsCache) return programsCache

  const response = await fetch('/content/programs.json')
  const data: Program[] = await response.json()
  programsCache = data
  return programsCache
}

export async function getProgram(id: string): Promise<Program | undefined> {
  const programs = await loadPrograms()
  return programs.find(p => p.id === id)
}

export async function getFreePrograms(): Promise<Program[]> {
  const programs = await loadPrograms()
  return programs.filter(p => !p.isPremium)
}

// Printables
export async function loadPrintables(): Promise<Printable[]> {
  if (printablesCache) return printablesCache

  const response = await fetch('/content/printables.json')
  const data: Printable[] = await response.json()
  printablesCache = data
  return printablesCache
}

export async function getPrintable(id: string): Promise<Printable | undefined> {
  const printables = await loadPrintables()
  return printables.find(p => p.id === id)
}

export async function getFreePrintables(): Promise<Printable[]> {
  const printables = await loadPrintables()
  return printables.filter(p => !p.isPremium)
}

export async function getPopularPrintables(): Promise<Printable[]> {
  const printables = await loadPrintables()
  return printables.filter(p => p.popular)
}

export async function getPrintablesByCategory(category: string): Promise<Printable[]> {
  const printables = await loadPrintables()
  if (category === 'all') return printables
  return printables.filter(p => p.category === category)
}

// Helpers for questionnaire results
export async function getProgramsForSituations(situationIds: string[]): Promise<Program[]> {
  const programs = await loadPrograms()
  return programs.filter((p) =>
    p.situationIds.some((sid) => situationIds.includes(sid))
  )
}

export async function getTopActivitiesForSituations(situationIds: string[], limit = 3): Promise<Activity[]> {
  const activities = await loadActivities()
  return activities
    .filter((a) => !a.isPremium && a.situationIds.some((sid) => situationIds.includes(sid)))
    .slice(0, limit)
}

// Calming techniques for SOS mode
export interface CalmingTechnique {
  id: string
  title: string
  description: string
  type: string
  duration: number
  steps: string[]
}

interface CalmingTechniquesData {
  techniques: CalmingTechnique[]
}

let calmingTechniquesCache: CalmingTechnique[] | null = null

export async function loadCalmingTechniques(): Promise<CalmingTechnique[]> {
  if (calmingTechniquesCache) return calmingTechniquesCache

  const response = await fetch('/content/calming-techniques.json')
  const data: CalmingTechniquesData = await response.json()
  calmingTechniquesCache = data.techniques
  return calmingTechniquesCache
}

// Toys
export async function loadToys(): Promise<Toy[]> {
  if (toysCache) return toysCache

  const response = await fetch('/content/toys.json')
  const data: ToysData = await response.json()
  toysCache = data.toys
  return toysCache
}

// Books
export async function loadBooks(): Promise<Book[]> {
  if (booksCache) return booksCache

  const response = await fetch('/content/books.json')
  const data: BooksData = await response.json()
  booksCache = data.books
  return booksCache
}

// Icon mapping for Lucide icons (fallback to emoji)
export const iconMap: Record<string, string> = {
  'running': '',
  'utensils': '',
  'cloud-rain': '',
  'moon': '',
  'volume-x': '',
  'shirt': '',
  'refresh-cw': '',
  'hand': '',
  'zap': '',
  'target': '',
  'heart-pulse': '',
  'sunrise': '',
  'person-running': '',
}

export function getIconEmoji(iconName: string): string {
  return iconMap[iconName] || ''
}

// DIY Activities
export async function loadDiyActivities(): Promise<DiyActivity[]> {
  if (diyActivitiesCache) return diyActivitiesCache

  const response = await fetch('/content/diy-activities.json')
  const data: DiyActivitiesData = await response.json()
  diyActivitiesCache = data.activities.map((raw) => ({
    ...transformActivity(raw as unknown as RawActivity),
    categoryId: raw.categoryId,
    ageRange: raw.ageRange,
  })) as DiyActivity[]
  return diyActivitiesCache
}

export async function loadDiyCategories(): Promise<DiyCategory[]> {
  if (diyCategoriesCache) return diyCategoriesCache

  const response = await fetch('/content/diy-activities.json')
  const data: DiyActivitiesData = await response.json()
  diyCategoriesCache = data.categories
  // Also populate activities cache if empty
  if (!diyActivitiesCache) {
    diyActivitiesCache = data.activities.map((raw) => ({
      ...transformActivity(raw as unknown as RawActivity),
      categoryId: raw.categoryId,
      ageRange: raw.ageRange,
    })) as DiyActivity[]
  }
  return diyCategoriesCache
}

export async function getDiyActivitiesForCategory(
  categoryId: string,
  ageMonths?: number
): Promise<DiyActivity[]> {
  const activities = await loadDiyActivities()
  let filtered = activities.filter(a => a.categoryId === categoryId)
  if (ageMonths !== undefined) {
    filtered = filtered.filter(a => ageMonths >= a.ageRange.min && ageMonths <= a.ageRange.max)
  }
  return filtered.sort((a, b) => a.order - b.order)
}
